'use client';

import * as React from 'react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Card, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/BackButton';
import { useToast } from '@/components/ui/use-toast';
import { useProductDetails } from '@/lib/api/products';
import { useUpdateVariant, type UpdateVariantInput } from '@/lib/api/variants';
import { Product } from '@tagadapay/crm-entities';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils';
import { VariantDetailsSkeleton } from './components';
import { VariantInformationForm, VariantPricesFormSection } from '@/components/form/variant';
import { variantSchema, type VariantFormData } from '../validation';
import { useStoreNavigate } from '@/hooks/use-app-navigate';

type VariantWithPrices = Product.ProductWithRelations['variants'][number];

const VariantDetailsPageContainer = () => {
  const { productId, variantId: variantId, id: storeId } = useParams();
  const { t } = useTranslation('products');
  const { data: products, isLoading, error } = useProductDetails(productId ? [productId] : [], !!productId);

  if (!productId || !variantId) {
    return null;
  }

  if (isLoading) {
    return <VariantDetailsSkeleton />;
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!products || products.length === 0) {
    return (
      <div className="container mx-auto py-8">
        <p>{t('productNotFound')}</p>
      </div>
    );
  }

  const product = products[0];
  const variant = product.variants.find((v) => v.id === variantId);

  if (!variant) {
    return (
      <div className="container mx-auto py-8">
        <p>{t('variantNotFound')}</p>
      </div>
    );
  }

  return <VariantEditPage variant={variant} product={product} storeId={storeId || ''} />;
};

const transformVariantToFormData = (variant: VariantWithPrices): VariantFormData => ({
  name: variant.name,
  description: variant.description || '',
  sku: variant.sku || null,
  imageUrl: variant.imageUrl || null,
  grams: variant.grams,
  active: variant.active ?? true,
  default: variant.default ?? false,
  prices: variant.prices,
  price: null,
  compareAtPrice: null,
  inStock: variant.inStock ?? true,
  creditPrice: variant.creditPrice ?? null,
});

const VariantEditPage = ({
  variant,
  product,
  storeId,
}: {
  variant: VariantWithPrices;
  product: Product.ProductWithRelations;
  storeId: string;
}) => {
  const { t } = useTranslation('products');
  const { storeRoutes, navigate } = useStoreNavigate();
  const { toast } = useToast();
  const updateVariantMutation = useUpdateVariant();
  const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null);

  const methods = useForm<VariantFormData>({
    defaultValues: transformVariantToFormData(variant),
    values: transformVariantToFormData(variant),
    resolver: zodResolver(variantSchema),
    mode: 'onBlur',
  });

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  const handleUpdateVariant = async (data: VariantFormData) => {
    try {
      setFormError(null);

      await updateVariantMutation.mutateAsync({
        variantId: variant.id,
        updatedData: data,
      });

      toast({
        title: t('variantUpdated'),
        description: t('variantUpdatedSuccess'),
      });
      navigate(`${storeRoutes.PRODUCT_DETAILS(product.id)}?tab=variants`);
    } catch (error) {
      const parsedError = parseTRPCError(error);
      setFormError(parsedError);

      if (!parsedError.isValidationError) {
        toast({
          title: t('updateError'),
          description: parsedError.message,
          variant: 'destructive',
        });
      }
    }
  };

  const handleCancel = () => {
    navigate(`${storeRoutes.PRODUCT_DETAILS(product.id)}?tab=variants`);
  };

  return (
    <div className="bg-background min-h-screen">
      <div className="flex flex-col space-y-4 sm:space-y-6">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleUpdateVariant)}>
            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-center gap-4">
                <BackButton to={storeRoutes.PRODUCT_DETAILS(product.id)} />
                <div>
                  <h1 className="text-2xl font-bold tracking-tight">{variant.name}</h1>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {t('editVariant')}
                  </p>
                  {product.externalProductId && (
                    <p className="text-muted-foreground mt-1 text-xs">
                      {t('externalProductId')}: {product.externalProductId}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-3">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    {t('cancel')}
                  </Button>
                  <Button type="submit" size="sm" disabled={isSubmitting || updateVariantMutation.isPending}>
                    {isSubmitting || updateVariantMutation.isPending
                      ? t('saving')
                      : t('saveChanges')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Error Display */}
            {formError && <ErrorDisplay error={formError} onDismiss={() => setFormError(null)} />}

            {/* Main Content */}
            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              {/* Main content - 1/2 width */}
              <div className="space-y-6">
                <VariantInformationForm storeId={storeId} />
              </div>

              {/* Sidebar - 1/2 width */}
              <div className="space-y-6">
                <VariantPricesFormSection storeId={storeId} />
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default withRequiredPermission(VariantDetailsPageContainer, PERMISSIONS.STORES_VIEW);
